import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useParams } from 'react-router';
import LoopRoundedIcon from '@mui/icons-material/LoopRounded';
import DrawOutlinedIcon from '@mui/icons-material/DrawOutlined';
import AddPhotoAlternateOutlinedIcon from '@mui/icons-material/AddPhotoAlternateOutlined';

import { useProject } from '../../../../context/ProjectContext';
import { changeAdImagesTable } from '../../../../redux/id8/id8Reducer';
import SearchInput from '../../../../components/common-inputs/SearchInput';
import WithLabel, { LABEL_STYLES } from '../../../../components/with-label/WithLabel';
import CommonDropdown from '../../../../components/common-dropdown/CommonDropdown';
import { IMAGES_PAGE_SIZE_OPTIONS } from '../../frames/filters/FrameFilters';
import TooltipButton from '../../../../components/tooltip-button/TooltipButton';
import { BUTTON_STYLES } from '../../../../components/common-button/CommonButton';
import { ID8_GEN_TYPES } from '../../../../constants';
import useImagesGenerationStatus from '../../../hooks/useImagesGenerationStatus';
import { MODIFY_CONCEPT_AD, RECREATE_AD, RUN_AGAIN_AD } from '../../../../redux/id8/id8Actions';
import PromptModal from '../../frames/prompt-modal/PromptModal';
import useConditionTooltip from '../../../../hooks/useConditionTooltip';
import {
  MODIFY_CONCEPT_AD as MODIFY_CONCEPT_AD_CONSUMER,
  RECREATE_AD as RECREATE_AD_CONSUMER,
  RUN_AGAIN_AD as RUN_AGAIN_AD_CONSUMER,
} from '../../../../redux/consumer/id8/consumerId8Actions';

import scss from './RecreateFilters.module.scss';

function RecreateFilters({ onShowUploadModal }) {
  const dispatch = useDispatch();
  const { t } = useTranslation('id8');
  const { id } = useParams();

  const { actions } = useProject();

  const [isShowModal, setIsShowModal] = useState(false);

  const adImagesTableSettings = useSelector((state) => state.id8.adImagesTableSettings);
  const isImagesExist = useSelector((state) => state.id8.adImagesContent.length > 0);
  const loadingActions = useSelector((state) => state.id8.loadingActions);
  const isImagesGenerating = useImagesGenerationStatus({ genType: ID8_GEN_TYPES.RECREATE });

  const isActionButtonsDisabled = !isImagesExist || isImagesGenerating;

  const buttonsTooltip = useConditionTooltip([
    {
      condition: !isImagesExist,
      message: t('noImages'),
    },
    {
      condition: isImagesGenerating,
      message: t('imagesGenerating'),
    },
  ]);

  const handleChangeSearch = (newSearch) => {
    dispatch(changeAdImagesTable({ searchQuery: newSearch, page: 1 }));
  };

  const handleChangeSize = (newSize) => {
    dispatch(changeAdImagesTable({ size: newSize }));
  };

  const handleRunAgain = () => {
    actions('runAgainAd', { projectId: id });
  };

  const handleOpenPromptModal = () => {
    setIsShowModal(true);
  };

  const handleClosePromptModal = () => {
    setIsShowModal(false);
  };

  const handleModifyConcept = async (values) => {
    actions('modifyAdConcept', {
      newConcept: values.description,
    });
    handleClosePromptModal();
  };

  useEffect(() => {
    actions('getAdImages', id);
  }, [
    actions,
    id,
    adImagesTableSettings.searchQuery,
    adImagesTableSettings.page,
    adImagesTableSettings.sort,
    adImagesTableSettings.size,
  ]);

  return (
    <div className={scss.wrapper}>
      <SearchInput value={adImagesTableSettings.searchQuery} onChange={handleChangeSearch} size='sm' maxWidth={364} />

      <div className={scss.rightSection}>
        <div className={scss.buttonsWrapper}>
          {isImagesExist ? (
            <TooltipButton
              styleColor={BUTTON_STYLES.PRIMARY}
              onClick={onShowUploadModal}
              disabled={isActionButtonsDisabled}
              tooltip={buttonsTooltip}
              isLoading={loadingActions[RECREATE_AD] || loadingActions[RECREATE_AD_CONSUMER]}
              maxWidth={192}
              LeftIcon={<AddPhotoAlternateOutlinedIcon />}>
              {t('recreateAd')}
            </TooltipButton>
          ) : null}

          <TooltipButton
            styleColor={BUTTON_STYLES.PRIMARY}
            onClick={handleRunAgain}
            disabled={isActionButtonsDisabled}
            isLoading={loadingActions[RUN_AGAIN_AD] || loadingActions[RUN_AGAIN_AD_CONSUMER]}
            tooltip={buttonsTooltip}
            maxWidth={192}
            LeftIcon={<LoopRoundedIcon />}>
            {t('runAgain')}
          </TooltipButton>

          <TooltipButton
            styleColor={BUTTON_STYLES.PRIMARY}
            onClick={handleOpenPromptModal}
            disabled={isActionButtonsDisabled}
            isLoading={loadingActions[MODIFY_CONCEPT_AD] || loadingActions[MODIFY_CONCEPT_AD_CONSUMER]}
            tooltip={buttonsTooltip}
            maxWidth={192}
            LeftIcon={<DrawOutlinedIcon />}>
            {t('modifyConcept')}
          </TooltipButton>
        </div>

        <WithLabel variant={LABEL_STYLES.ROW} label={t('show')} maxWidth={168}>
          <CommonDropdown
            value={adImagesTableSettings.size}
            onSelect={handleChangeSize}
            options={IMAGES_PAGE_SIZE_OPTIONS}
          />
        </WithLabel>
      </div>

      <PromptModal
        isOpen={isShowModal}
        onClose={handleClosePromptModal}
        onCancel={handleClosePromptModal}
        onSubmit={handleModifyConcept}
        title={t('addYourChanges')}
      />
    </div>
  );
}

export default RecreateFilters;
